/* P4P image viewer: one-image-at-a-time product photo lightbox. See sections/p4p-image-viewer.liquid. */
if (!customElements.get('p4p-image-viewer')) {
  const WIDTHS = [360, 540, 720, 900, 1080, 1296, 1512, 1728, 2048, 2400, 3000, 3600];
  const THUMB_WIDTH = 120;
  const TAP_SLOP = 8;
  const SWIPE_DISTANCE = 50;
  const ZOOM_THRESHOLD = 0.9;

  const sizedUrl = (src, width) => {
    const url = new URL(src, window.location.href);
    url.searchParams.set('width', width);
    return url.href;
  };

  const srcsetFor = (image) =>
    WIDTHS.filter((width) => width < image.width)
      .concat(image.width)
      .map((width) => `${sizedUrl(image.src, width)} ${width}w`)
      .join(', ');

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pinchZoomed = () => window.visualViewport && window.visualViewport.scale > 1.01;

  customElements.define(
    'p4p-image-viewer',
    class P4PImageViewer extends HTMLElement {
      connectedCallback() {
        if (!this.initialized) this.initialize();
        document.addEventListener('click', this.onDocumentClick, true);
        window.addEventListener('popstate', this.onPopState);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick, true);
        window.removeEventListener('popstate', this.onPopState);
      }

      initialize() {
        this.initialized = true;

        this.images = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
        this.imagesById = new Map(this.images.map((image) => [String(image.id), image]));
        this.dialog = this.querySelector('dialog');
        this.stage = this.querySelector('[data-viewer-stage]');
        this.canvas = this.querySelector('[data-viewer-canvas]');
        this.image = this.querySelector('[data-viewer-image]');
        this.thumbs = this.querySelector('[data-viewer-thumbs]');
        this.zoomButton = this.querySelector('[data-viewer-zoom]');
        this.zoomLabel = this.querySelector('[data-viewer-zoom-label]');
        this.prevButton = this.querySelector('[data-viewer-prev]');
        this.nextButton = this.querySelector('[data-viewer-next]');
        this.closeButton = this.querySelector('[data-viewer-close]');
        this.currentLabel = this.querySelector('[data-viewer-current]');
        this.totalLabel = this.querySelector('[data-viewer-total]');
        this.status = this.querySelector('[data-viewer-status]');

        this.slides = [];
        this.thumbButtons = [];
        this.index = 0;
        this.zoomed = false;
        this.loadToken = 0;
        this.gesture = null;
        this.lastGestureMoved = false;

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onPopState = this.onPopState.bind(this);

        this.closeButton.addEventListener('click', () => this.close());
        this.prevButton.addEventListener('click', () => this.show(this.index - 1));
        this.nextButton.addEventListener('click', () => this.show(this.index + 1));
        this.zoomButton.addEventListener('click', () => this.setZoom(!this.zoomed));
        this.thumbs.addEventListener('click', (event) => {
          const thumb = event.target.closest('[data-index]');
          if (thumb) this.show(Number(thumb.dataset.index));
        });
        this.dialog.addEventListener('keydown', this.onKeydown.bind(this));
        this.dialog.addEventListener('click', this.onDialogClick.bind(this));
        this.dialog.addEventListener('close', this.onClosed.bind(this));
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.close();
        });
        this.stage.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.stage.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.stage.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.stage.addEventListener('pointercancel', this.onPointerCancel.bind(this));

        this.resizeObserver = new ResizeObserver(() => this.layout());
        this.resizeObserver.observe(this.stage);
      }

      // Take over clicks on product images in this product's main gallery. Videos and 3D models keep Dawn's modal.
      onDocumentClick(event) {
        const toggle = event.target.closest && event.target.closest('.product__modal-opener--image .product__media-toggle');
        const opener = toggle && toggle.closest('modal-opener');
        if (!opener) return;
        const modalSelector = opener.getAttribute('data-modal') || '';
        const productInfo = toggle.closest('product-info');
        if (!modalSelector.startsWith('#ProductModal-') || toggle.closest('quick-add-modal')) return;
        if (!productInfo || productInfo.dataset.productId !== this.dataset.productId) return;
        if (!this.imagesById.has(toggle.dataset.mediaId)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.open(modalSelector.replace('#ProductModal-', ''), toggle.dataset.mediaId, toggle);
      }

      // Follow the gallery's visible order so hidden variant images stay hidden.
      collectSlides() {
        const prefix = `${this.sectionId}-`;
        const list = document.getElementById(`Slider-Gallery-${this.sectionId}`);
        const slides = [];
        if (list) {
          list.querySelectorAll(':scope > [data-media-id]').forEach((item) => {
            const id = item.dataset.mediaId.startsWith(prefix) ? item.dataset.mediaId.slice(prefix.length) : item.dataset.mediaId;
            const image = this.imagesById.get(id);
            if (image && !slides.includes(image)) slides.push(image);
          });
        }
        return slides.length ? slides : this.images.slice();
      }

      open(sectionId, mediaId, opener) {
        this.sectionId = sectionId;
        this.opener = opener;
        this.slides = this.collectSlides();
        this.openedIndex = Math.max(0, this.slides.findIndex((slide) => String(slide.id) === mediaId));
        this.totalLabel.textContent = this.slides.length;
        this.dialog.classList.toggle('is-single', this.slides.length < 2);
        this.renderThumbs();
        this.image.style.visibility = 'hidden';

        document.documentElement.classList.add('p4p-viewer-lock');
        this.isOpen = true;
        this.dialog.showModal();
        this.closeButton.focus({ preventScroll: true });
        if (this.awaitingPop) this.pushAfterPop = true;
        else this.pushHistory();
        this.show(this.openedIndex);
      }

      // Lets the browser's Back button close the viewer instead of leaving the page.
      pushHistory() {
        if (window.history.state && window.history.state.p4pImageViewer) return;
        window.history.pushState(Object.assign({}, window.history.state, { p4pImageViewer: true }), '');
        this.pushedHistory = true;
      }

      close() {
        if (this.dialog.open) this.dialog.close();
        this.onClosed();
      }

      // Runs once however the dialog closes: the X, Esc, a backdrop click, or the browser's Back button.
      onClosed() {
        if (!this.isOpen) return;
        this.isOpen = false;
        document.documentElement.classList.remove('p4p-viewer-lock');
        this.loadToken += 1;
        this.setZoom(false);
        this.dialog.classList.remove('is-loading');
        if (this.pushedHistory) {
          this.pushedHistory = false;
          if (!this.closingFromHistory && window.history.state && window.history.state.p4pImageViewer) {
            this.awaitingPop = true;
            window.history.back();
          }
        }
        this.pushAfterPop = false;
        this.closingFromHistory = false;

        let focusTarget = this.opener;
        const slide = this.slides[this.index];
        const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
        if (slide && this.index !== this.openedIndex && gallery && typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(`${this.sectionId}-${slide.id}`, false);
          focusTarget = document.querySelector(`#Slide-${this.sectionId}-${slide.id} .product__media-toggle`) || focusTarget;
        }
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      }

      onPopState() {
        if (this.awaitingPop) {
          this.awaitingPop = false;
          if (this.pushAfterPop && this.isOpen) this.pushHistory();
          this.pushAfterPop = false;
          return;
        }
        if (!this.dialog.open || (window.history.state && window.history.state.p4pImageViewer)) return;
        this.closingFromHistory = true;
        this.close();
      }

      renderThumbs() {
        const total = this.slides.length;
        this.thumbButtons = this.slides.map((slide, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'p4p-viewer__thumb';
          button.dataset.index = index;
          button.setAttribute('aria-label', `Show image ${index + 1} of ${total}`);
          const thumb = document.createElement('img');
          thumb.src = sizedUrl(slide.src, Math.min(THUMB_WIDTH, slide.width));
          thumb.alt = '';
          thumb.loading = 'lazy';
          thumb.width = 56;
          thumb.height = Math.round((56 * slide.height) / slide.width);
          button.append(thumb);
          return button;
        });
        this.thumbs.replaceChildren(...this.thumbButtons);
        this.thumbs.hidden = total < 2;
      }

      show(index) {
        if (!this.slides.length) return;
        const total = this.slides.length;
        this.index = clamp(index, 0, total - 1);
        const slide = this.slides[this.index];
        const position = this.index + 1;

        this.setZoom(false);
        this.currentLabel.textContent = position;
        this.status.textContent = `Image ${position} of ${total}`;
        this.prevButton.disabled = this.index === 0;
        this.nextButton.disabled = this.index === total - 1;
        this.thumbButtons.forEach((button, buttonIndex) => button.setAttribute('aria-current', String(buttonIndex === this.index)));
        this.scrollThumbIntoView();

        const token = ++this.loadToken;
        const next = document.createElement('img');
        next.className = 'p4p-viewer__image';
        next.dataset.slideId = slide.id;
        next.alt = slide.alt || `${this.dataset.title}, image ${position} of ${total}`;
        next.decoding = 'async';
        next.draggable = false;
        next.width = slide.width;
        next.height = slide.height;
        this.sizeImage(next, slide);
        next.srcset = srcsetFor(slide);
        next.src = sizedUrl(slide.src, Math.min(slide.width, 1080));

        this.dialog.classList.add('is-loading');
        const swap = () => {
          if (token !== this.loadToken || this.image === next) return;
          this.image.replaceWith(next);
          this.image = next;
          this.layout();
        };
        const loaded = () => {
          swap();
          if (token === this.loadToken) this.dialog.classList.remove('is-loading');
        };
        next.addEventListener('load', loaded, { once: true });
        next.addEventListener('error', loaded, { once: true });
        next.decode().then(loaded, loaded);
        if (this.image.style.visibility === 'hidden' || !this.image.dataset.slideId) swap();
        else window.setTimeout(swap, 300);

        this.preload(this.index + 1);
        this.preload(this.index - 1);
      }

      preload(index) {
        const slide = this.slides[index];
        if (!slide) return;
        const image = new Image();
        image.sizes = `${this.fitSize(slide).width}px`;
        image.srcset = srcsetFor(slide);
      }

      scrollThumbIntoView() {
        const button = this.thumbButtons[this.index];
        if (!button || this.thumbs.hidden) return;
        const left = button.offsetLeft - (this.thumbs.clientWidth - button.offsetWidth) / 2;
        this.thumbs.scrollTo({ left, behavior: reducedMotion() ? 'auto' : 'smooth' });
      }

      // Fit the image inside the stage without enlarging it past its native size.
      fitSize(slide) {
        const style = window.getComputedStyle(this.canvas);
        const width = this.stage.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
        const height = this.stage.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
        const scale = Math.min(width / slide.width, height / slide.height, 1);
        return {
          width: Math.max(1, Math.floor(slide.width * scale)),
          height: Math.max(1, Math.floor(slide.height * scale)),
          scale,
        };
      }

      sizeImage(image, slide) {
        const size = this.zoomed ? { width: slide.width, height: slide.height } : this.fitSize(slide);
        image.style.width = `${size.width}px`;
        image.style.height = `${size.height}px`;
        image.sizes = `${size.width}px`;
      }

      // The displayed image lags the current slide while the next one loads.
      currentImageShows(slide) {
        return Boolean(slide) && this.image.dataset.slideId === String(slide.id);
      }

      layout() {
        const slide = this.slides[this.index];
        if (!this.dialog.open || !slide) return;
        const zoomable = this.fitSize(slide).scale < ZOOM_THRESHOLD;
        this.dialog.classList.toggle('is-zoomable', zoomable);
        this.zoomButton.hidden = !zoomable;
        if (!zoomable && this.zoomed) this.setZoom(false);
        if (this.currentImageShows(slide)) this.sizeImage(this.image, slide);
      }

      // Switch between fit-to-screen and full size, keeping the clicked point in view.
      setZoom(zoomed, point) {
        const slide = this.slides[this.index];
        if (zoomed === this.zoomed || (zoomed && !this.dialog.classList.contains('is-zoomable'))) return;
        let focusX = 0.5;
        let focusY = 0.5;
        if (zoomed && point) {
          const rect = this.image.getBoundingClientRect();
          focusX = clamp((point.x - rect.left) / rect.width, 0, 1);
          focusY = clamp((point.y - rect.top) / rect.height, 0, 1);
        }

        this.zoomed = zoomed;
        this.dialog.classList.toggle('is-zoomed', zoomed);
        this.zoomButton.setAttribute('aria-pressed', String(zoomed));
        this.zoomLabel.textContent = zoomed ? 'Fit image to screen' : 'Zoom to full size';
        if (this.currentImageShows(slide)) this.sizeImage(this.image, slide);

        if (zoomed && slide) {
          const style = window.getComputedStyle(this.canvas);
          this.stage.scrollLeft = parseFloat(style.paddingLeft) + focusX * slide.width - this.stage.clientWidth / 2;
          this.stage.scrollTop = parseFloat(style.paddingTop) + focusY * slide.height - this.stage.clientHeight / 2;
        } else {
          this.stage.scrollLeft = 0;
          this.stage.scrollTop = 0;
        }
      }

      onKeydown(event) {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        const actions = {
          ArrowLeft: () => this.show(this.index - 1),
          ArrowRight: () => this.show(this.index + 1),
          Home: () => this.show(0),
          End: () => this.show(this.slides.length - 1),
        };
        if (!actions[event.key]) return;
        event.preventDefault();
        actions[event.key]();
      }

      // A click on the image toggles zoom. A click on the empty area around it closes the viewer.
      onDialogClick(event) {
        if (this.lastGestureMoved) {
          this.lastGestureMoved = false;
          return;
        }
        if (event.target === this.image) {
          this.setZoom(!this.zoomed, { x: event.clientX, y: event.clientY });
        } else if (event.target === this.stage || event.target === this.canvas || event.target === this.dialog) {
          this.close();
        }
      }

      onPointerDown(event) {
        this.lastGestureMoved = false;
        if (!event.isPrimary) {
          this.endGesture();
          return;
        }
        if (event.target.closest('button') || (event.pointerType === 'mouse' && event.button !== 0)) return;
        this.gesture = {
          id: event.pointerId,
          type: event.pointerType,
          x: event.clientX,
          y: event.clientY,
          scrollLeft: this.stage.scrollLeft,
          scrollTop: this.stage.scrollTop,
          moved: false,
        };
      }

      onPointerMove(event) {
        const gesture = this.gesture;
        if (!gesture || gesture.id !== event.pointerId) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        if (Math.abs(dx) > TAP_SLOP || Math.abs(dy) > TAP_SLOP) gesture.moved = true;

        if (this.zoomed) {
          if (gesture.type === 'mouse' && gesture.moved) {
            if (!this.stage.hasPointerCapture(gesture.id)) {
              this.stage.setPointerCapture(gesture.id);
              this.dialog.classList.add('is-panning');
            }
            this.stage.scrollLeft = gesture.scrollLeft - dx;
            this.stage.scrollTop = gesture.scrollTop - dy;
          }
          return;
        }
        if (gesture.type !== 'mouse' && gesture.moved && this.slides.length > 1 && !pinchZoomed()) {
          this.dialog.classList.add('is-dragging');
          this.canvas.style.transform = `translateX(${dx}px)`;
        }
      }

      onPointerUp(event) {
        const gesture = this.gesture;
        if (!gesture || gesture.id !== event.pointerId) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        const swiped =
          !this.zoomed &&
          gesture.type !== 'mouse' &&
          !pinchZoomed() &&
          Math.abs(dx) > SWIPE_DISTANCE &&
          Math.abs(dx) > Math.abs(dy) * 1.5;
        this.lastGestureMoved = gesture.moved;
        this.endGesture();
        if (swiped) this.show(this.index + (dx < 0 ? 1 : -1));
      }

      onPointerCancel() {
        if (this.gesture) this.lastGestureMoved = true;
        this.endGesture();
      }

      endGesture() {
        if (this.gesture && this.stage.hasPointerCapture(this.gesture.id)) this.stage.releasePointerCapture(this.gesture.id);
        this.gesture = null;
        this.dialog.classList.remove('is-panning', 'is-dragging');
        this.canvas.style.transform = '';
      }
    }
  );
}
